import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { TYP, type BindingPayload, type DecisionPayload, type MandatePayload } from '@pryme/protocol';
import { createPryme, randomB64 } from '@pryme/sdk';
import { createAgent, resolveAgentConfig, type AgentCredentials, type AgentIntent, type MandateProof } from '@pryme/sdk/agent';
import { Alert, Badge, formatMinor, formatTime, Icon, Spinner, useAsyncAction } from '@pryme/ui';
import { CodeBlock } from '../components/CodeBlock';
import { ErrorBox } from '../components/ErrorBox';
import { IdentityStatusBadge } from '../components/IdentityStatus';
import { JwsCard } from '../components/JwsCard';
import { SecretKeyInput } from '../components/SecretKeyInput';
import { useServiceOutlet } from '../components/ServiceLayout';
import { pretty } from '../lib/files';
import { userPortalUrl, apiOrigin } from '../lib/portal';
import { identityStatusFromBinding, inspectJws, issuerKeysOf, matchSecret, prymeKeysOf } from '../lib/signing';

interface CheckResponse {
  outcome: 'allow' | 'approval_required' | 'deny';
  code?: string;
  reason?: string;
  authorization_id?: string;
  approval_id?: string;
  approval_expires_at?: string;
  operation_hash?: string;
  reserved?: number;
  decision: string | null;
  idempotent?: boolean;
}

/** The agent secret key (sk_test_… / sk_live_…) the agent got from pairing or the developer panel. */
function parseCreds(text: string): { creds?: AgentCredentials; error?: string } {
  if (!text.trim()) return {};
  try {
    return { creds: resolveAgentConfig(text.trim()) };
  } catch (e) {
    return { error: (e as Error).message.replace(/^.*?: invalid Pryme secret key: /, 'Not a valid agent secret key: ').replace(/^Pryme agent configuration: /, '') };
  }
}

const newOrderId = () => `ord_${randomB64(6)}`;

export function MandatePlayground() {
  const { service: s } = useServiceOutlet();
  // Memory-only secrets
  const [credsText, setCredsText] = useState('');
  const [showCreds, setShowCreds] = useState(false);
  const [secret, setSecret] = useState('');
  const { creds, error: credsError } = parseCreds(credsText);
  const skMatch = matchSecret(secret, s.keys);
  // Exactly what a backend on this secret key trusts for Pryme's responses: its own generation's verification key.
  const prymeKeys = prymeKeysOf(s, skMatch.ok ? skMatch.key : undefined);

  const [proofs, setProofs] = useState<MandateProof[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fetchM = useAsyncAction();

  const [action, setAction] = useState('');
  const [amount, setAmount] = useState('2499');
  const [currency, setCurrency] = useState('DKK');
  const [orderId, setOrderId] = useState(newOrderId);
  const [merchant, setMerchant] = useState('');
  const [resource, setResource] = useState('');
  // The agent signs the exact operation (Pryme-Intent). Off: your service asks on its own (ADR-0015).
  const [agentSigned, setAgentSigned] = useState(true);
  const [result, setResult] = useState<{ res: CheckResponse; request: Record<string, unknown>; responseVerified: boolean } | null>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [watch, setWatch] = useState(false);
  const check = useAsyncAction();
  const settle = useAsyncAction();
  const [settled, setSettled] = useState<string | null>(null);

  const mine = proofs?.filter((p) => p.recipient === s.id) ?? [];
  const others = proofs?.filter((p) => p.recipient !== s.id) ?? [];
  const selected = mine.find((p) => p.mandateId === selectedId) ?? null;

  // The identity binding is verified with Pryme's attestation keys, whatever secret key the backend uses.
  const binding = inspectJws<BindingPayload>(selected?.binding, issuerKeysOf(s), TYP.binding);
  const bp = binding?.verified ? binding.decoded!.payload : undefined;
  const pairwise = useMemo(() => (bp ? [{ keyId: bp.pairwise_kid, publicKey: bp.pairwise_key }] : []), [bp]);
  const mandate = inspectJws<MandatePayload>(selected?.mandate, pairwise, TYP.mandate);
  const mp = mandate?.verified ? mandate.decoded!.payload : undefined;

  useEffect(() => {
    if (!selected) return;
    setAction(selected.permissions[0] ?? '');
    const c = selected.constraints as { currency?: string };
    if (c.currency) setCurrency(c.currency);
    setResult(null);
    setApprovalId(null);
    setOrderId(newOrderId());
  }, [selected?.mandateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMandates = async () => {
    if (!creds) return;
    const agent = createAgent({ ...creds, apiBase: apiOrigin() });
    const r = await fetchM.run(() => agent.mandates());
    if (r) {
      setProofs(r);
      const first = r.find((p) => p.recipient === s.id);
      setSelectedId(first?.mandateId ?? null);
    }
  };

  const pryme = () =>
    createPryme({
      apiBase: apiOrigin(),
      serviceId: s.id,
      apiAudience: s.apiAudience,
      signingKey: { keyId: skMatch.ok ? skMatch.key.id : '', secretKey: skMatch.ok ? skMatch.signingSecret : '' },
      prymeKeys,
      environment: s.environment,
    });

  const context = () => {
    const ctx: Record<string, unknown> = { action };
    if (amount.trim()) ctx.amount = Number(amount);
    if (currency.trim()) ctx.currency = currency.trim().toUpperCase();
    if (orderId.trim()) ctx.orderId = orderId.trim();
    if (merchant.trim()) ctx.merchant = merchant.trim();
    if (resource.trim()) ctx.resource = resource.trim();
    return ctx;
  };

  const runCheck = async (withApproval?: string | null) => {
    if (!bp || !selected || !creds || !skMatch.ok) return;
    const ctx = context();
    // Playing the agent: it signs exactly this operation for this service, as agent.request({ intent }) does.
    const agentIntent = agentSigned
      ? createAgent({ ...creds, apiBase: apiOrigin() }).signIntent({ recipient: s.id, proof: selected, operation: ctx as unknown as AgentIntent }).intent
      : undefined;
    const request = {
      subject: bp.subject, mandateId: selected.mandateId, agentId: creds.agentId, context: ctx,
      ...(agentIntent ? { agentIntent } : {}),
      ...(withApproval ? { approvalId: withApproval } : {}),
    };
    let verified = false;
    const r = await check.run(async () => {
      const p = createPryme({
        apiBase: apiOrigin(), serviceId: s.id, apiAudience: s.apiAudience,
        signingKey: { keyId: skMatch.key.id, secretKey: skMatch.signingSecret }, prymeKeys, environment: s.environment,
        onEvent: (e) => { if (e.type === 'response.signature_verified') verified = true; },
      });
      return p.call<CheckResponse>('/v1/authorizations/check', request);
    });
    if (r) {
      setResult({ res: r, request, responseVerified: verified });
      if (r.outcome === 'approval_required' && r.approval_id) setApprovalId(r.approval_id);
      if (r.outcome !== 'approval_required') setWatch(false);
    } else setWatch(false);
  };

  // Watch the human's real decision: retry with the approvalId every 5 s while pending.
  const runRef = useRef(runCheck);
  runRef.current = runCheck;
  useEffect(() => {
    if (!watch || !approvalId) return;
    let n = 0;
    const t = setInterval(() => {
      if (++n > 60) {
        setWatch(false);
        return;
      }
      if (document.visibilityState === 'visible') void runRef.current(approvalId);
    }, 5000);
    return () => clearInterval(t);
  }, [watch, approvalId]);

  const decision = result ? inspectJws<DecisionPayload>(result.res.decision, pairwise, TYP.decision) : null;
  const dp = decision?.verified ? decision.decoded!.payload : undefined;
  const decisionChecks = dp && creds && selected
    ? [
        { label: `aud = ${s.id}`, ok: dp.aud === s.id },
        { label: 'subject matches the binding', ok: dp.subject === bp?.subject },
        { label: 'mandate_id matches', ok: dp.mandate_id === selected.mandateId },
        { label: 'agent_id matches', ok: dp.agent_id === creds.agentId },
        { label: 'not expired', ok: dp.exp * 1000 > Date.now() },
      ]
    : [];

  const amountNum = Number(amount);
  const amountValid = !amount.trim() || (Number.isSafeInteger(amountNum) && amountNum >= 0);

  return (
    <>
      <section className="p-card" aria-labelledby="mp-h">
        <h2 id="mp-h">Mandate playground</h2>
        <p className="p-small p-muted">
          Play both sides: an <strong>agent</strong> fetches the mandates its person signed, and <strong>your service</strong> runs the online, transaction-specific check. Over-limit
          actions create a real approval request for the person in the Pryme portal.
        </p>
        {s.status !== 'active' && <Alert tone="warn">Activate this service first: <code>/v1/authorizations/check</code> only accepts active services.</Alert>}
        <Alert tone="info">
          Need an agent? In the Pryme portal under <a href={userPortalUrl('/agents')}>Agents</a>, open <em>For developers</em>, register a sandbox agent and copy its
          agent secret key, then grant it a permission with <strong>{s.displayName}</strong> as the service. The person must have connected to this service first
          (“Continue with Pryme”). Real agents pair instead and keep their key to themselves.
        </Alert>

        <div className="p-grid">
          <div className="p-field">
            <label htmlFor="agent-creds">Agent secret key (PRYME_AGENT_SECRET_KEY)</label>
            <textarea
              id="agent-creds"
              className="p-textarea"
              rows={3}
              spellCheck={false}
              autoComplete="off"
              value={credsText}
              onChange={(e) => setCredsText(e.target.value)}
              placeholder="sk_test_…"
              style={showCreds || !credsText ? undefined : ({ WebkitTextSecurity: 'disc' } as CSSProperties)}
              aria-describedby="agent-creds-hint"
            />
            <div className="p-row">
              {credsText && <button type="button" className="p-btn secondary small" onClick={() => setShowCreds((x) => !x)} aria-pressed={showCreds}>{showCreds ? 'Mask' : 'Show'}</button>}
              {credsText && <button type="button" className="p-btn ghost small" onClick={() => { setCredsText(''); setProofs(null); }}>Forget</button>}
            </div>
            <span className="p-hint" id="agent-creds-hint" style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}><Icon name="lock" size={13} /><span>Held in memory only. Used to sign the agent’s request locally; never sent to Pryme or stored.</span></span>
            {credsError && <span className="p-hint" style={{ color: 'var(--p-bad)' }}>{credsError}</span>}
            {creds && <Badge tone="ok">Agent {creds.agentId} · key {creds.keyId}</Badge>}
          </div>
          <div>
            <SecretKeyInput value={secret} onChange={setSecret} keys={s.keys} label="Your secret key (PRYME_SECRET_KEY), for the merchant-side check" />
          </div>
        </div>
        <button className="p-btn" disabled={!creds || fetchM.busy} onClick={fetchMandates}>
          {fetchM.busy ? <Spinner label="Fetching (agent-signed GET /v1/agents/self/mandates)…" /> : 'Fetch agent mandates'}
        </button>
        <ErrorBox error={fetchM.error} />
      </section>

      {proofs && (
        <section className="p-card" aria-labelledby="ml-h">
          <h2 id="ml-h">Mandates held by the agent</h2>
          {mine.length === 0 && <Alert tone="warn">No active mandate for <strong>{s.displayName}</strong> ({s.id}). {others.length > 0 && `The agent holds ${others.length} mandate(s) for other services.`}</Alert>}
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="p-skip">Choose a mandate</legend>
            {mine.map((p) => (
              <label key={p.mandateId} className="p-check">
                <input type="radio" name="mandate" checked={selectedId === p.mandateId} onChange={() => setSelectedId(p.mandateId)} />
                <span>
                  <code>{p.mandateId}</code> · {p.permissions.map((x) => <code key={x} className="p-mono-chip" style={{ marginRight: 4 }}>{x}</code>)}
                  <span className="p-small p-muted" style={{ display: 'block' }}>Constraints {JSON.stringify(p.constraints)} · expires {formatTime(p.expiresAt)}</span>
                </span>
              </label>
            ))}
            {others.map((p) => (
              <div key={p.mandateId} className="p-small p-muted" style={{ padding: '0.25rem 0' }}>
                <code>{p.mandateId}</code> for {p.recipientName} ({p.recipient}) · not for this service
              </div>
            ))}
          </fieldset>
        </section>
      )}

      {selected && (
        <section className="p-card" aria-labelledby="mv-h">
          <h2 id="mv-h">Verify the agent's proof</h2>
          <IdentityStatusBadge status={identityStatusFromBinding(bp)} />
          <div className="p-grid" style={{ marginTop: '0.75rem' }}>
            <JwsCard title="Binding (Pryme attestation key → pairwise key)" inspection={binding} keyLabel="Pryme attestation key" />
            <JwsCard title="Mandate (signed by pairwise key)" inspection={mandate} keyLabel="pairwise" />
          </div>
          {mp && (
            <p className="p-small">
              The mandate authorizes agent <code>{mp.agent_id}</code> (key <code>{mp.agent_kid}</code>) to <strong>{mp.permissions.join(', ')}</strong> at <code>{mp.aud}</code>
              {mp.requires_approval_for.length > 0 && <>, and always needs approval for {mp.requires_approval_for.join(', ')}</>}. Nothing else.
            </p>
          )}
        </section>
      )}

      {selected && bp && (
        <section className="p-card" aria-labelledby="ck-h">
          <h2 id="ck-h">Merchant action: <code>POST /v1/authorizations/check</code></h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setApprovalId(null);
              setWatch(false);
              setSettled(null);
              void runCheck(null);
            }}
          >
            <div className="p-grid">
              <div className="p-field">
                <label htmlFor="act">Action</label>
                <input id="act" className="p-input" list="act-list" value={action} onChange={(e) => setAction(e.target.value)} required />
                <datalist id="act-list">{selected.permissions.map((p) => <option key={p} value={p} />)}</datalist>
              </div>
              <div className="p-field">
                <label htmlFor="amt">Amount (minor units)</label>
                <input id="amt" className="p-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!amountValid} />
                <span className="p-hint">{amountValid && amount.trim() && currency ? `= ${formatMinor(amountNum, currency.toUpperCase())}` : 'Integer øre/cents, e.g. 2499 = 24.99'}</span>
              </div>
              <div className="p-field">
                <label htmlFor="cur">Currency</label>
                <input id="cur" className="p-input" value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value)} />
              </div>
              <div className="p-field">
                <label htmlFor="ord">Order ID</label>
                <div className="p-row" style={{ flexWrap: 'nowrap' }}>
                  <input id="ord" className="p-input mono" value={orderId} onChange={(e) => setOrderId(e.target.value)} />
                  <button type="button" className="p-btn secondary small" onClick={() => setOrderId(newOrderId())}>New</button>
                </div>
                <span className="p-hint">Idempotency key: the same orderId with different parameters is denied.</span>
              </div>
              <div className="p-field">
                <label htmlFor="mer">Merchant (optional)</label>
                <input id="mer" className="p-input" value={merchant} onChange={(e) => setMerchant(e.target.value)} />
              </div>
              <div className="p-field">
                <label htmlFor="res">Resource (optional)</label>
                <input id="res" className="p-input" value={resource} onChange={(e) => setResource(e.target.value)} />
              </div>
            </div>
            <label className="p-check">
              <input type="checkbox" checked={agentSigned} onChange={(e) => setAgentSigned(e.target.checked)} />
              <span>
                Signed by the agent (<code>Pryme-Intent</code>)
                <span className="p-small p-muted" style={{ display: 'block' }}>
                  {agentSigned
                    ? 'The agent signs exactly this operation for this service, and your check forwards it. Pryme uses its permission for this, once.'
                    : 'Your service asks on its own. Pryme can only ask the person: the agent’s permission never allows this by itself.'}
                </span>
              </span>
            </label>
            <button className="p-btn" disabled={check.busy || !skMatch.ok || !action || !amountValid}>
              {check.busy && !watch ? <Spinner label="Checking…" /> : 'Check authorization'}
            </button>
            {!skMatch.ok && <span className="p-small p-muted"> Paste your secret key above to sign the check.</span>}
          </form>
          <ErrorBox error={check.error} />

          {result && (
            <div style={{ marginTop: '1rem' }}>
              <div className="p-row" role="status" aria-live="polite">
                <span className={`dv-outcome ${result.res.outcome === 'allow' ? 'allow' : result.res.outcome === 'deny' ? 'deny' : 'approval'}`} data-testid="mandate-outcome">
                  <Icon name={result.res.outcome === 'allow' ? 'check' : result.res.outcome === 'deny' ? 'x' : 'clock'} size={20} strokeWidth={2} />
                  {result.res.outcome === 'allow' ? 'Allowed' : result.res.outcome === 'deny' ? 'Denied' : 'Approval required'}
                </span>
                {result.res.code && <code className="p-mono-chip">{result.res.code}</code>}
                {result.res.reason && <span className="p-small">{result.res.reason}</span>}
                {result.res.idempotent && <Badge tone="neutral">Idempotent replay of an earlier decision</Badge>}
                <Badge tone={result.responseVerified ? 'ok' : 'bad'} icon={result.responseVerified ? 'check' : 'x'}>{result.responseVerified ? 'Response signed by Pryme' : 'Response signature not verified'}</Badge>
              </div>

              {result.res.outcome === 'approval_required' && (
                <Alert tone="warn">
                  Pryme created approval <code>{result.res.approval_id}</code>
                  {result.res.approval_expires_at && <> (expires {formatTime(result.res.approval_expires_at)})</>}. The person must approve <em>this exact operation</em>.
                  <div className="p-row" style={{ marginTop: '0.5rem' }}>
                    <a className="p-btn small" href={userPortalUrl('/approvals')} target="_blank" rel="noreferrer">Open Approvals in the Pryme portal <Icon name="external" size={13} /></a>
                    <button type="button" className="p-btn secondary small" disabled={check.busy} onClick={() => void runCheck(approvalId)}>Retry with approval</button>
                    <label className="p-check" style={{ padding: 0 }}>
                      <input type="checkbox" checked={watch} onChange={(e) => setWatch(e.target.checked)} />
                      <span className="p-small">Watch for the person’s decision (retries every 5 s)</span>
                    </label>
                    {watch && <Spinner label="Watching…" />}
                  </div>
                </Alert>
              )}

              {result.res.outcome === 'allow' && result.res.authorization_id && (
                <div className="p-row" style={{ margin: '0.75rem 0' }}>
                  <span className="p-small">Authorization <code>{result.res.authorization_id}</code>{typeof result.res.reserved === 'number' && <> reserved {formatMinor(result.res.reserved, currency.toUpperCase() || 'DKK')}</>}. Report the outcome:</span>
                  {(['settled', 'released'] as const).map((o) => (
                    <button
                      key={o}
                      type="button"
                      className="p-btn secondary small"
                      disabled={settle.busy || !!settled}
                      onClick={async () => {
                        const r = await settle.run(() => pryme().call<{ state: string }>(`/v1/authorizations/${encodeURIComponent(result.res.authorization_id!)}/settle`, { subject: bp.subject, outcome: o }));
                        if (r) setSettled(r.state);
                      }}
                    >
                      {o === 'settled' ? 'Settle (order completed)' : 'Release (order cancelled)'}
                    </button>
                  ))}
                  {settled && <Badge tone="ok">{settled}</Badge>}
                </div>
              )}
              <ErrorBox error={settle.error} />

              <div className="p-grid" style={{ marginTop: '0.75rem' }}>
                <div>
                  <CodeBlock label="Signed request body" code={pretty(result.request)} />
                  <CodeBlock label="Response" code={pretty({ ...result.res, decision: result.res.decision ? `${result.res.decision.slice(0, 32)}…` : null })} />
                </div>
                <div>
                  <JwsCard title="Decision (pryme-decision-v1)" inspection={decision} keyLabel="pairwise" />
                  {decisionChecks.length > 0 && (
                    <ul className="dv-check-list">{decisionChecks.map((c) => <li key={c.label} className={c.ok ? 'ok' : 'bad'}>{c.label}</li>)}</ul>
                  )}
                  <p className="p-small p-muted">The decision is signed with the person’s pairwise key for your service: not a Pryme key and not the agent’s key.</p>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}
